import React, { useRef, useState } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useToast } from '~/lib/ui/toast'
import { apiPost } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { Textarea } from '~/lib/ui/textarea'
import { useCandidate, queryClient } from './CandidateProvider'
import { Label } from '~/lib/ui/label'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useResumeReview } from './ResumeReviewDetail'
import { useServiceRequests } from './ServicesSection'
import PrivacySettings from './PrivacySettings'
import YnsIcon from '../icons/YnsIcon'
import { Dropzone, AddMoreFiles } from './ResumeDropzones'
import { getFlexNoteDefaults } from './getFlexNoteDefaults'
import { getPremiumNoteDefaults } from './getPremiumNoteDefaults'
import RecommendedFileAttachments from './RecommendedFileAttachments'
import InitiateServiceRequestForm from './InitiateServiceRequestForm'
import { useTranslation } from '~/hooks/useTranslation'

export const DurationSelect = ({ onValueChange }) => {
  const durationOptions = [1, 2, 5, 10, 15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(false)
  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }
  return (
    <div className="flex flex-row justify-evenly">
      <Select onValueChange={handleValueChange} className="w-full">
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className="ml-2 w-full"
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
        />
      )}
    </div>
  )
}

const ErrorMessages = ({
  isRevisionDialog,
  noteToCandidate,
  duration,
  resumeFile,
  noteDetails,
  isPreWorkDialog,
  fileTypeName,
}) => {
  const noteNotEdited =
    isRevisionDialog && noteToCandidate?.toUpperCase().includes('COACH TO EDIT')

  const noNote =
    isRevisionDialog &&
    (noteToCandidate?.trim() === undefined || noteToCandidate?.trim() === '')

  const noNoteDetails =
    isPreWorkDialog &&
    (noteDetails?.trim() === undefined || noteDetails?.trim() === '')

  const noDuration =
    !duration || duration === 0 || duration === '0' || duration === ''

  if (noteNotEdited)
    return (
      <span>
        <b>Save Disabled:</b> Address the &quot;COACH TO EDIT&quot; text in your
        note to the candidate.
      </span>
    )
  if (noNoteDetails)
    return (
      <span>
        <b>Save Disabled:</b> Please add note details.
      </span>
    )
  if (noDuration) {
    return (
      <span>
        <b>Save Disabled:</b> Please select a duration.
      </span>
    )
  }
  if (noNote || !resumeFile.name)
    return (
      <span>
        <b>Save Disabled:</b> {`Please select a ${fileTypeName} File.`}
      </span>
    )
  return ''
}

const UploadResumeRevision = ({
  revisionNumber,
  isFinalRevision = false,
  type,
  children,
  createRequest = false,
}) => {
  const { candidateId, coachId, detailItemId } = useCandidate()
  const { t } = useTranslation('')
  const [open, setOpen] = useState(false)
  const [resumeFile, setResumeFile] = useState({})
  const [noteDetails, setNoteDetails] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [formValues, setFormValues] = useState({})
  const onFileSelected = (file) => {
    setResumeFile(file)
  }
  const [files, setFiles] = useState([])
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [duration, setDuration] = useState('')
  const { toast } = useToast()

  const isPreWorkDialog = type && type === 'Pre-Work'
  const isRevisionDialog = !isPreWorkDialog
  const fileTypeName = isPreWorkDialog ? 'Pre-Work' : 'Resume Revision'

  const controller = isRevisionDialog ? 'resume_revisions' : 'pre_works'

  const postResumeRevision = async (formData) => {
    setIsSaving(true)
    const postPath = `/coach/candidates/${candidateId}/${controller}.json`
    const response = await apiPost(postPath, formData, true)
    return response
  }

  const createRevisionMutation = useMutation({
    mutationFn: postResumeRevision,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: `${fileTypeName} created successfully`,
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error('Error creating resume revision:', error)
      const description =
        error?.response?.data?.error ||
        'An error occurred while uploading the resume.'
      console.log('Error description:', description)
      toast({
        description,
        title: 'Could Not Upload Resume',
        variant: 'destructive',
        duration: 10_000,
      })
    },
  })

  const createRevision = async (fromCreate = false) => {
    if (isSaving && !fromCreate) return null

    const revisionType = isPreWorkDialog
      ? 'pre_work'
      : 'resume_revision_coach_revision'

    const topics = isPreWorkDialog ? 'Pre-Work' : 'Edit/Review'

    const f = new FormData()
    f.append(`${revisionType}[candidate_id]`, candidateId)
    f.append(`${revisionType}[coach_id]`, coachId)
    f.append(`${revisionType}[resume_review_id]`, detailItemId)
    {
      isRevisionDialog &&
        f.append(`resume_revision_coach_revision[notes]`, noteToCandidate)
    }
    if (isRevisionDialog && isFinalRevision) {
      f.append(`${revisionType}[close_review]`, 1)
    }

    f.append(`${revisionType}[coach_note_attributes][duration]`, duration)
    f.append(`${revisionType}[coach_note_attributes][topics][Resume][]`, topics)
    formPrivacySettings.forEach((setting) => {
      f.append(
        `${revisionType}[coach_note_attributes][privacy_settings][]`,
        setting,
      )
    })
    f.append(`${revisionType}[coach_note_attributes][note]`, noteDetails)
    f.append(`${revisionType}[remote_content]`, resumeFile)

    attachmentKeys.forEach((key) => {
      f.append(`default_attachment_keys[]`, key)
    })

    files.forEach((file, idx) => {
      f.append(
        `${revisionType}[service_request_revisions_documents_attributes][${idx}][uploaded_document]`,
        file,
      )
    })
    createRevisionMutation.mutate(f)
  }

  const saveClick = () => {
    if (createRequest) {
      createResumeReview()
    } else {
      createRevision()
    }
  }

  const resumeReview = useResumeReview()
  const { defaultFeedbackAttachmentKeys } = resumeReview
  const { meetingViaCalendly } = resumeReview.candidateRevisions?.[0] || false

  const { serviceRequests } = useServiceRequests()
  const currentResumeReview = serviceRequests?.find(
    (serviceRequest) => serviceRequest.id == detailItemId,
  )
  const { programLevel } = useCandidate()
  const idRef = useRef(null)

  const createResumeReview = async () => {
    if (isSaving) return

    setIsSaving(true)

    const f = new FormData()
    Object.keys(formValues).forEach((key) => {
      f.append(`service_request[${key}]`, formValues[key])
    })
    f.append('service_request[candidate_id]', candidateId)
    f.append('service_request[type]', 'ServiceRequest::ResumeReview')

    const postPath = `/coach/service_requests.json`
    const response = await apiPost(postPath, f, true)
    if (response.status === 200) {
      idRef.current = response.data.service_request.id
      createRevision(true)
    } else {
      toast({
        title: 'Error',
        description: `Failed to save revision`,
        variant: 'destructive',
      })
    }
  }

  const noteDefaults =
    programLevel === 'PREMIUM' ||
    programLevel === 'ADVANCE' ||
    programLevel === 'SELECT' ||
    programLevel === 'CONCIERGE'
      ? getPremiumNoteDefaults(
          meetingViaCalendly,
          revisionNumber,
          resumeReview.coachName,
          resumeReview.candidateFirstName,
          currentResumeReview,
          isFinalRevision,
          t,
        )
      : getFlexNoteDefaults(
          meetingViaCalendly,
          revisionNumber,
          resumeReview.coachName,
          resumeReview.candidateFirstName,
          currentResumeReview,
          isFinalRevision,
          t,
        )

  const [noteToCandidate, setNoteToCandidate] = useState(noteDefaults)

  const handleRemoveResumeFile = () => setResumeFile({})

  const resetForm = () => {
    setResumeFile({})
    setNoteDetails('')
    setPrivacySettings([])
    setIsSaving(false)
    setFiles([])
    setDuration('')
    setNoteToCandidate(noteDefaults)
  }

  const handleDialogOpenChange = (isOpen) => {
    setOpen(isOpen)
    if (!isOpen) {
      resetForm()
    }
  }

  const [attachmentKeys, setAttachmentKeys] = useState(
    defaultFeedbackAttachmentKeys || [],
  )

  const onCheckedChange = (key) => (checked) => {
    if (checked) {
      setAttachmentKeys([...attachmentKeys, key])
    } else {
      setAttachmentKeys(attachmentKeys.filter((k) => k !== key))
    }
  }

  const noteNotEdited =
    isRevisionDialog && noteToCandidate?.toUpperCase().includes('COACH TO EDIT')

  const noNote =
    isRevisionDialog &&
    (noteToCandidate?.trim() === undefined || noteToCandidate?.trim() === '')

  const noDuration =
    !duration || duration === 0 || duration === '0' || duration === ''

  const preWorkInvalid = noDuration || noteDetails.length <= 0
  const revisionInvalid =
    !resumeFile.name || noteNotEdited || noNote || noDuration

  const formIsInvalid = isPreWorkDialog ? preWorkInvalid : revisionInvalid

  return (
    <Dialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">
              {isPreWorkDialog ? 'New Pre-Work' : 'New Resume Revision'}
            </h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            {isPreWorkDialog
              ? 'Upload Pre-Work For this Candidate'
              : 'Create a new Resume Revision for this candidate.'}
          </DialogDescription>
          {createRequest && (
            <InitiateServiceRequestForm
              formValues={formValues}
              setFormValues={setFormValues}
              requestType={'ResumeReview'}
            />
          )}
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes) (Required)
            </Label>
            <DurationSelect onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          {isRevisionDialog && (
            <div className="py-4">
              <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
                Please record any remarks with this update. Comments will be
                viewed by the candidate.
              </Label>
              <Textarea
                className="min-h-56 w-full"
                name="noteToCandidate"
                value={noteToCandidate}
                onChange={(e) => setNoteToCandidate(e.target.value)}
              />
            </div>
          )}
          <div>
            {isRevisionDialog && (
              <RecommendedFileAttachments
                defaultFeedbackAttachmentKeys={defaultFeedbackAttachmentKeys}
                attachmentKeys={attachmentKeys}
                onCheckedChange={onCheckedChange}
              />
            )}
            <Dropzone preWork={isPreWorkDialog} onFileSelected={onFileSelected}>
              {resumeFile.name ? (
                <>
                  <div className="mb-4 flex items-center">
                    <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
                    <span>
                      {`${resumeFile.name} (${(resumeFile.size / 1024).toFixed(2)} KB)${resumeFile.type ? ` (${resumeFile.type})` : ''}`}
                    </span>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveResumeFile()
                      }}
                      variant="ghost"
                      className="ml-2 text-red-600 hover:text-red-400"
                    >
                      <YnsIcon name="trash" className="h-6 w-6" />
                    </Button>
                  </div>
                  <p>
                    Drag and drop a new file to replace the current selection.
                  </p>
                </>
              ) : (
                <p>
                  {`Drag and drop the ${fileTypeName} here, or click to select`}
                </p>
              )}
            </Dropzone>
            <AddMoreFiles setFiles={setFiles} />
          </div>
          <div className="flex flex-row justify-end">
            <Button
              className="inline"
              onClick={saveClick}
              disabled={isSaving || formIsInvalid}
            >
              Save
            </Button>
          </div>
          <p className="mt-2 flex flex-row justify-end text-red-500">
            <ErrorMessages
              isRevisionDialog={isRevisionDialog}
              noteToCandidate={noteToCandidate}
              duration={duration}
              resumeFile={resumeFile}
              noteDetails={noteDetails}
              isPreWorkDialog={isPreWorkDialog}
              fileTypeName={fileTypeName}
            />
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default UploadResumeRevision
